import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getAllBlogs } from "../../../controllers/homeController";

// 🔹 Normalize URL helper (from ArticleCard)
function normalizeUrl(u = "") {
  const s = u.trim();
  if (!s) return "";
  const withProto = s.startsWith("//") ? `https:${s}` : s;
  try {
    const url = new URL(withProto);
    url.pathname = url.pathname
      .split("/")
      .map((seg) => encodeURIComponent(decodeURIComponent(seg)))
      .join("/");
    return url.toString();
  } catch {
    return encodeURI(withProto);
  }
}

// 🔹 Slugify function for clean readable slugs
function slugify(title = "") {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "")
    .trim();
}

const NewsSection = () => {
  const [articles, setArticles] = useState([]);

  function formatDate(v) {
    try {
      const d = new Date(v);
      return isNaN(d)
        ? ""
        : d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
    } catch {
      return "";
    }
  }

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await getAllBlogs();

        let blogs = [];
        if (Array.isArray(response)) {
          blogs = response;
        } else if (response?.data) {
          blogs = response.data;
        }

        blogs.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
        setArticles(blogs.slice(0, 4));
      } catch (err) {
        console.error("Error fetching blogs:", err);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <section className="bg-background dark:bg-gray-800  text-textLight dark:text-textDark py-16 sm:py-24 transition-colors duration-300">
      <div className="w-full mx-auto max-w-[1400px] 2xl:max-w-[1600px] px-6 xl:px-20 flex flex-col items-center text-center">
        {/* 🔹 Top Section */}
        <motion.p
          className="text-textLight/70 dark:text-textDark/70 text-sm font-semibold mb-3"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Latest Industry Insights
        </motion.p>

        <motion.h2
          className="font-bold tracking-tight text-2xl sm:text-4xl mb-4 dark:text-textDark"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Stay Ahead: Key Industry News You Can't Miss
        </motion.h2>

        <motion.p
          className="text-textLight/80 dark:text-textDark/80 mb-8 max-w-2xl"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Discover essential updates and insights that empower players to make
          informed decisions in the ever-evolving landscape of the industry.
        </motion.p>

        {/* Mascot Image */}
        {/* <motion.img
          src={mascot}
          alt="Mascot"
          className="w-36 sm:w-48 mb-10 select-none pointer-events-none"
          draggable={false}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        /> */}

        {/* 🔹 Bottom: Articles Grid */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-6">
          {articles.length === 0 ? (
            <p className="col-span-4 text-center text-gray-500">
              Loading articles...
            </p>
          ) : (
            articles.map((item, i) => {
              // ✅ Use a clean blog-style slug (like ArticleCard)
              const cleanSlug = slugify(item.title || "");
              const slugLink = normalizeUrl(`/blog/${cleanSlug || item.id}`);

              return (
                <motion.article
                  key={item.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  className="bg-white dark:bg-darkbg  border border-gray-100 dark:border-none rounded-3xl overflow-hidden flex flex-col transition-all duration-300"
                >
                  {/* Image (Clickable) */}
                  <a href={slugLink} className="block">
                    <div className="aspect-[4/3] w-full overflow-hidden">
                      <img
                        loading="eager"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        src={item.imageUrl}
                        alt={item.title}
                        className="block w-full h-full object-cover transform hover:scale-[1.03] transition-transform duration-300"
                      />
                    </div>
                  </a>

                  {/* Text Section */}
                  <div className="py-5 px-6 flex flex-col items-start text-left">
                    <a href={slugLink} className="no-underline">
                      <h3
                        className="text-[15px] sm:text-base font-semibold leading-snug dark:text-textDark text-left mb-2 hover:text-primary transition-colors duration-300"
                        style={{
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.title}
                      </h3>
                    </a>

                    <p className="text-textLight/70 dark:text-textDark/60 text-xs">
                      {formatDate(item.publishedAt)}
                    </p>
                  </div>
                </motion.article>
              );
            })
          )}
        </div>

        {/* Button */}
        <motion.div
          className="mt-10"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <a
            href="/blogs"
            className="inline-block font-medium text-primary border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-[#2a2f3a] transition-colors duration-300"
          >
            View all articles
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default NewsSection;
